import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Container, Tab, Tabs } from "@mui/material";
import { useGetOrgByIdQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useGetUsersByOrgAndStatusQuery, useGetBudgetRequestsQuery } from "state/rtkQuery/user/userQueries";

import AdminDashboard from "../../AdminDashboard";
import UlbRequestsTable, { UlbRequestFilters } from "../UlbRequests/UlbRequestsTable";

const ACCENT = "#26890d";

// Both tabs are "requests waiting on an Owner/Approver" — org join requests
// (existing AdminDashboard table, untouched) and AIC budget change requests
// (UlbRequestsTable) — grouped under one route/page instead of two, with the
// tab strip replacing the old static "Pending Approvals" label. The ULB tab
// mirrors AdminDashboard's own header markup (org name + search box) exactly.
const PendingApprovalsTabs = () => {
  const { orgId } = useParams();
  const { data: orgData } = useGetOrgByIdQuery(orgId);
  const { data: platformData } = useGetPlatformsQuery();
  const platformId = platformData?._id;
  const [tab, setTab] = useState(0);
  const [ulbSearchQuery, setUlbSearchQuery] = useState("");
  const [ulbSearchField, setUlbSearchField] = useState("");
  const [ulbStatusFilter, setUlbStatusFilter] = useState("pending");

  // Switching what's being searched by clears whatever was typed/picked for
  // the previous field — an email string isn't a valid request_date or
  // action value (or vice versa), so carrying it over would just query
  // with a stale, meaningless value. Action is one of the fields Filter By
  // can target now (searchField === "action"), not its own separate filter.
  const handleUlbSearchFieldChange = (value) => {
    setUlbSearchField(value);
    setUlbSearchQuery("");
  };

  // Dedicated, lightweight count-only queries (perPage: 1, only `.total` is
  // read) — independent of whichever tab's actual content is mounted below,
  // so both tab labels get a count immediately on load without needing to
  // keep both tabs' heavy components (and their DataGrids) alive at once.
  // Deliberately NOT reusing AdminDashboard's/UlbRequestsTable's own queries:
  // an earlier version kept both components always-mounted to share their
  // queries, but that made whichever tab was hidden via display:none report
  // a 0px DataGrid height (MUI warns/mis-sizes on unhide) — plain, separate
  // queries here avoid that entirely.
  const { data: pendingApprovalsCountData } = useGetUsersByOrgAndStatusQuery(
    { orgId, status: "Pending", platformId, page: 1, perPage: 1 },
    { skip: !orgId || !platformId, refetchOnMountOrArgChange: true }
  );
  const { data: ulbPendingCountData } = useGetBudgetRequestsQuery(
    { orgId, platformId, action: "all", status: "pending", page: 1, perPage: 1 },
    { skip: !orgId || !platformId, refetchOnMountOrArgChange: true }
  );
  const pendingApprovalsCount = pendingApprovalsCountData?.total ?? null;
  const ulbPendingCount = ulbPendingCountData?.total ?? null;

  const tabLabel = (label, count) => (count == null ? label : `${label} (${count})`);

  const tabsSlot = (
    <Tabs
      value={tab}
      onChange={(e, v) => setTab(v)}
      sx={{
        minHeight: 36,
        mb: 1,
        ml: 0,
        "& .MuiTab-root": {
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.9rem",
          minHeight: 36,
          padding: "0 !important",
          minWidth: "0 !important",
          marginRight: "1.5rem",
        },
        "& .Mui-selected": { color: `${ACCENT} !important` },
        "& .MuiTabs-indicator": { backgroundColor: ACCENT },
      }}
    >
      <Tab label={tabLabel("Pending Approvals", pendingApprovalsCount)} />
      <Tab label={tabLabel("AIC Budget Requests", ulbPendingCount)} />
    </Tabs>
  );

  if (tab === 1) {
    return (
      <Container sx={{ maxWidth: "1300px !important", flex: "1", marginTop: "3rem" }}>
        <Box sx={{ height: 600 }}>
          <div className="admin-headerSearchFlex">
            <div className="admin-dashboard-header">
              <span className="admin-dashboard-header-content">
                <strong>{orgData?.name}</strong>
              </span>
            </div>
            <div className="admin-dashboard-search">
              <UlbRequestFilters
                statusFilter={ulbStatusFilter}
                onStatusFilterChange={setUlbStatusFilter}
                searchField={ulbSearchField}
                onSearchFieldChange={handleUlbSearchFieldChange}
                searchQuery={ulbSearchQuery}
                onSearchQueryChange={setUlbSearchQuery}
              />
            </div>
          </div>
          {tabsSlot}
          <UlbRequestsTable
            searchQuery={ulbSearchQuery}
            onSearchQueryChange={setUlbSearchQuery}
            statusFilter={ulbStatusFilter}
            onStatusFilterChange={setUlbStatusFilter}
            searchField={ulbSearchField}
            onSearchFieldChange={handleUlbSearchFieldChange}
          />
          <div
            className="admin-dashboard-support-container"
            style={{ marginTop: "30px", padding: "10px 0px", paddingBottom: "2rem" }}
          >
            <p>
              Please fill our{" "}
              <a
                href="https://deloitteglobal.service-now.com/sp?id=sc_cat_item&sys_id=4a417b4f1b17855cbcae55b0604bcb1c"
                target="_blank"
                className="request-support-link"
                rel="noreferrer"
              >
                Support Request
              </a>
              &nbsp; Form if you face any issues.
            </p>
          </div>
        </Box>
      </Container>
    );
  }

  return <AdminDashboard tabsSlot={tabsSlot} />;
};

export default PendingApprovalsTabs;
